<template>
	<div class="tabs-component w-full">
		<div class="tabs-nav border-b border-border bg-white relative">
			<button
				v-if="isScrollable"
				@click="scrollTabs('left')"
				class="absolute left-0 top-0 bottom-0 z-20 flex items-center justify-center w-8 bg-white border-r border-border shadow-md transition-all duration-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
				:disabled="!canScrollLeft"
			>
				<svg class="w-4 h-4 text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
				</svg>
			</button>

			<button
				v-if="isScrollable"
				@click="scrollTabs('right')"
				class="absolute right-0 top-0 bottom-0 z-20 flex items-center justify-center w-8 bg-white border-l border-border shadow-md transition-all duration-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
				:disabled="!canScrollRight"
			>
				<svg class="w-4 h-4 text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
				</svg>
			</button>

			<div class="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory" ref="tabsContainer">
				<button
					v-for="(tab, index) in tabs"
					:key="index"
					@click="selectTab(index)"
					class="tab-button whitespace-nowrap px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium transition-all duration-200 relative snap-center flex-shrink-0 min-w-fit touch-manipulation"
					:class="[
						activeTab === index
							? 'text-primary border-b-2 border-primary'
							: 'text-text-secondary hover:text-text-primary',
					]"
				>
					<span class="flex items-center gap-1.5 sm:gap-2">
						<i v-if="tab.icon" :class="tab.icon" class="text-xs sm:text-sm"></i>
						<span class="truncate">{{ tab.label }}</span>
						<span
							v-if="tab.count !== undefined"
							class="inline-flex items-center justify-center min-w-[18px] sm:min-w-[20px] h-4.5 sm:h-5 px-1 sm:px-1.5 text-xs font-semibold rounded-full flex-shrink-0"
							:class="[
								activeTab === index
									? 'bg-primary text-white'
									: 'bg-gray-200 text-text-secondary',
							]"
						>
							{{ tab.count }}
						</span>
					</span>
				</button>
			</div>
		</div>

		<div class="tabs-content mt-4 sm:mt-6 px-1 sm:px-0">
			<transition :name="transitionName" mode="out-in">
				<div :key="activeTab" class="tab-panel">
					<slot :name="`tab-${activeTab}`" :tab="tabs[activeTab]">
						<div class="text-text-secondary">
							No content available
						</div>
					</slot>
				</div>
			</transition>
		</div>
	</div>
</template>

<script>
export default {
	name: 'TabsComponent',
	props: {
		tabs: {
			type: Array,
			required: true,
			default: () => [],
			validator: (tabs) => {
				return (
					Array.isArray(tabs) && tabs.every((tab) => tab && tab.label)
				);
			},
		},
		defaultTab: {
			type: Number,
			default: 0,
		},
		animated: {
			type: Boolean,
			default: true,
		},
	},
	data() {
		return {
			activeTab: this.defaultTab,
			transitionName: 'fade',
			isScrollable: false,
			canScrollLeft: false,
			canScrollRight: false,
		};
	},
	methods: {
		selectTab(index) {
			if (this.activeTab !== index) {
				this.transitionName = this.animated ? 'fade' : '';
				this.activeTab = index;
				this.$emit('tab-change', {
					index,
					tab: this.tabs[index],
				});
				this.scrollToActiveTab();
			}
		},
		checkScrollability() {
			const nav = this.$el.querySelector('.tabs-nav');
			const container = this.$refs.tabsContainer;
			if (nav && container) {
				const scrollWidth = container.scrollWidth;
				const clientWidth = container.clientWidth;
				this.isScrollable = scrollWidth > clientWidth;
				
				if (this.isScrollable) {
					nav.classList.add('scrollable');
					this.updateScrollButtons();
				} else {
					nav.classList.remove('scrollable');
					this.canScrollLeft = false;
					this.canScrollRight = false;
				}
			}
		},
		updateScrollButtons() {
			const container = this.$refs.tabsContainer;
			if (container) {
				this.canScrollLeft = container.scrollLeft > 0;
				this.canScrollRight = container.scrollLeft < container.scrollWidth - container.clientWidth;
			}
		},
		scrollTabs(direction) {
			const container = this.$refs.tabsContainer;
			if (!container) return;
			
			const scrollAmount = 200;
			if (direction === 'left') {
				container.scrollBy({
					left: -scrollAmount,
					behavior: 'smooth'
				});
			} else {
				container.scrollBy({
					left: scrollAmount,
					behavior: 'smooth'
				});
			}
		},
		scrollToActiveTab() {
			const nav = this.$el.querySelector('.tabs-nav');
			const activeButton = nav && nav.querySelector('.tab-button:nth-child(' + (this.activeTab + 1) + ')');
			
			if (nav && activeButton && this.isScrollable) {
				const navRect = nav.getBoundingClientRect();
				const buttonRect = activeButton.getBoundingClientRect();
				
				if (buttonRect.left < navRect.left || buttonRect.right > navRect.right) {
					activeButton.scrollIntoView({
						behavior: 'smooth',
						block: 'nearest',
						inline: 'center',
					});
				}
			}
		},
	},
	watch: {
		defaultTab(newVal) {
			this.activeTab = newVal;
		},
		tabs: {
			handler() {
				this.$nextTick(() => {
					this.checkScrollability();
				});
			},
			deep: true,
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.checkScrollability();
		});
		window.addEventListener('resize', this.checkScrollability);
		
		const container = this.$refs.tabsContainer;
		if (container) {
			container.addEventListener('scroll', this.updateScrollButtons);
		}
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.checkScrollability);
		
		const container = this.$refs.tabsContainer;
		if (container) {
			container.removeEventListener('scroll', this.updateScrollButtons);
		}
	},
};
</script>

<style scoped>
.tabs-component {
	width: 100%;
}

.tabs-nav {
	background: white;
	position: relative;
}

.tabs-nav::before,
.tabs-nav::after {
	content: '';
	position: absolute;
	top: 0;
	bottom: 0;
	width: 20px;
	pointer-events: none;
	z-index: 10;
	opacity: 0;
	transition: opacity 0.3s ease;
}

.tabs-nav::before {
	left: 0;
	background: linear-gradient(to right, white, transparent);
}

.tabs-nav::after {
	right: 0;
	background: linear-gradient(to left, white, transparent);
}

.tabs-nav.scrollable::before,
.tabs-nav.scrollable::after {
	opacity: 1;
}

.scrollbar-hide {
	-ms-overflow-style: none;
	scrollbar-width: none;
	scroll-behavior: smooth;
	-webkit-overflow-scrolling: touch;
}

.scrollbar-hide::-webkit-scrollbar {
	display: none;
}

.scrollbar-hide {
	padding-left: 0;
	padding-right: 0;
}

.tabs-nav.scrollable .scrollbar-hide {
	padding-left: 32px;
	padding-right: 32px;
}

.tab-button {
	border-bottom: 2px solid transparent;
	margin-bottom: -1px;
	outline: none;
	min-height: 44px;
}

.tab-button:focus {
	outline: none;
}

@media (hover: none) and (pointer: coarse) {
	.tab-button:active {
		background-color: rgba(0, 0, 0, 0.05);
		transform: scale(0.98);
	}
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
	transition: all 0.3s ease;
}

.slide-enter-from {
	opacity: 0;
	transform: translateX(10px);
}

.slide-leave-to {
	opacity: 0;
	transform: translateX(-10px);
}

@media (max-width: 640px) {
	.tabs-content {
		-webkit-overflow-scrolling: touch;
	}
}
</style>
